import { useQuery } from '@tanstack/react-query';
import { ScheduleSummaryRequest, ScheduleSummaryResponse, ScheduleSummaryError } from '../../../types/calendar-view';

type UseScheduleSummaryOptions = Readonly<{
  view: ScheduleSummaryRequest['view'];
  calendarDateFrom: string;
  calendarDateTo: string;
  status?: string;
}>;

const fetchScheduleSummary = async (request: ScheduleSummaryRequest): Promise<ScheduleSummaryResponse> => {
  const url = new URL('/api/services/schedule-summary', globalThis.location.origin);

  if (request.from === '' || request.to === '') {
    throw new Error('Date range (from and to) is required');
  }

  Object.entries(request).forEach(([key, value]) => {
    url.searchParams.append(key, String(value));
  });

  const response = await fetch(url.toString());

  if (!response.ok) {
    let errorMessage = 'Failed to fetch schedule summary';
    try {
      const errorData: ScheduleSummaryError = await response.json();
      errorMessage = errorData.title || errorData.message || errorMessage;
    } catch {
      errorMessage = response.statusText || errorMessage;
    }

    throw new Error(errorMessage);
  }

  return await response.json();
};

export const useScheduleSummary = ({ view, calendarDateFrom, calendarDateTo, status }: UseScheduleSummaryOptions) => {
  const enabled = Boolean(calendarDateFrom) && Boolean(calendarDateTo);

  return useQuery({
    queryKey: ['scheduleSummary', view, calendarDateFrom, calendarDateTo, status],
    enabled,
    queryFn: () =>
      fetchScheduleSummary({
        view,
        from: calendarDateFrom,
        to: calendarDateTo,
        ...(status ? { status } : {}),
      }),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });
};
